import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, useTheme, Portal } from "@mui/material";
import { CheckStatusLabel } from "../../utils/enums";

export const ComplianceProgressBar = ({ data }) => {
  const theme = useTheme();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);

  const totalRecords = data?.totalRecords || 1;
  const passRecords = data?.totalPassRecords || 0;
  const failRecords = data?.totalFailRecords || 0;
  const disabledRecords = data?.totalDisabledRecords || 0;
  const pendingRecords = data?.totalPendingRecords || 0;

  const passWidth = (passRecords / totalRecords) * 100;
  const failWidth = (failRecords / totalRecords) * 100;
  const disabledWidth = (disabledRecords / totalRecords) * 100;
  const pendingWidth = (pendingRecords / totalRecords) * 100;

  const totalPercentage = Math.round((passRecords / totalRecords) * 100);

  const handleMouseEnter = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          minWidth: 120,
          position: "relative",
          cursor: "pointer",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="body2" color="text.primary" fontWeight="bold">
            {`${totalPercentage}%`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Complete
          </Typography>
        </Box>

        {/* Multi-colored progress bar */}
        <Box
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: theme.customColors.border.subtle,
            overflow: "hidden",
            display: "flex",
          }}
        >
          {/* Passing checks - Green */}
          <Box
            sx={{
              width: `${passWidth}%`,
              backgroundColor: theme.customColors.status.success.main,
              height: "100%",
            }}
          />
          {/* Failing checks - Red */}
          <Box
            sx={{
              width: `${failWidth}%`,
              backgroundColor: theme.customColors.status.error.main,
              height: "100%",
            }}
          />
          {/* Disabled checks - Orange */}
          <Box
            sx={{
              width: `${disabledWidth}%`,
              backgroundColor: theme.customColors.status.warning.main,
              height: "100%",
            }}
          />
          {/* Pending checks - Blue */}
          <Box
            sx={{
              width: `${pendingWidth}%`,
              backgroundColor: theme.customColors.status.info.main,
              height: "100%",
            }}
          />
        </Box>
      </Box>
      {/* Tooltip with detailed breakdown - rendered in Portal */}
      {showTooltip && (
        <Portal>
          <Box
            sx={{
              position: "fixed",
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateX(-50%)",
              p: 2,
              backgroundColor: theme.customColors.background.main,
              borderRadius: 1,
              boxShadow: theme.customColors.shadow.main,
              border: `1px solid ${theme.customColors.border.main}`,
              zIndex: 10000000,
              minWidth: 200,
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            {/* Arrow pointing up */}
            <Box
              sx={{
                position: "absolute",
                top: -6,
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderBottom: `6px solid ${theme.customColors.background.main}`,
              }}
            />

            {/* Status labels with counts */}
            {[
              {
                label: CheckStatusLabel.PASSING,
                color: theme.customColors.status.success.main,
                value: passWidth.toFixed(1),
                count: passRecords,
              },
              {
                label: CheckStatusLabel.FAILING,
                color: theme.customColors.status.error.main,
                value: failWidth.toFixed(1),
                count: failRecords,
              },
              {
                label: CheckStatusLabel.DISABLED,
                color: theme.customColors.status.warning.main,
                value: disabledWidth.toFixed(1),
                count: disabledRecords,
              },
              {
                label: CheckStatusLabel.PENDING,
                color: theme.customColors.status.info.main,
                value: pendingWidth.toFixed(1),
                count: pendingRecords,
              },
            ].map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: item.color,
                      mr: 1,
                    }}
                  />
                  <Typography variant="body2" color="text.primary">
                    {item.label}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="text.primary"
                  >
                    {item.value}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({item.count})
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Portal>
      )}
    </>
  );
};
